import { useState } from "react";
import styles from "./programnotavailble.module.css";

// ── Data ──
const program = {
  emoji: "🧘",
  name: "Menopause Wellness Program",
  short: "Support for hot flashes, sleep changes, mood changes, vaginal dryness, bone health, and personalized menopause guidance.",
  clinic: "Bhubaneswar Clinic",
};

const reasons = [
  { icon: "🚫", title: "Not enabled at this clinic", desc: "The program is not yet enabled at Bhubaneswar Clinic." },
  { icon: "👩‍⚕️", title: "Specialist availability limited", desc: "Menopause specialists may not be available at this location." },
  { icon: "🗓️", title: "Launching soon", desc: "We're working to bring this program to your clinic soon." },
  { icon: "📍", title: "Location-specific services", desc: "This program may be available at select clinics." },
];

const alternatives = [
  {
    icon: "🩺",
    bg: "#fff7ed",
    title: "Book a Gynecology Consultation",
    desc: "Consult a gynecologist to discuss your symptoms and get expert guidance.",
    cta: "Book Consultation",
    ctaVariant: "Primary",
  },
  {
    icon: "📞",
    bg: "#fdf2f8",
    title: "Request a Care Coordinator Callback",
    desc: "Our care coordinator will call you to understand your needs and guide you.",
    cta: "Request Callback",
    ctaVariant: "Secondary",
  },
  {
    icon: "🔍",
    bg: "#f5f0ff",
    title: "Explore Other Women's Health Programs",
    desc: "Browse other programs that may support your health goals.",
    cta: "View Other Programs",
    ctaVariant: "Ghost",
  },
];

const similarPrograms = [
  {
    emoji: "🛡️",
    name: "Preventive Women's Health Program",
    desc: "Routine screenings, HPV care, and early detection for long-term wellness.",
    tags: ["Reproductive Age", "Preventive Care"],
  },
  {
    emoji: "🌸",
    name: "Perimenopause Care Pathway",
    desc: "Personalized support for hormonal changes, symptoms, and lifestyle balance.",
    tags: ["Perimenopause", "Hormonal Health"],
  },
  {
    emoji: "🦴",
    name: "Bone & Hormonal Wellness Program",
    desc: "Bone density health, calcium guidance, and hormone balance support.",
    tags: ["Perimenopause", "Bone Health"],
  },
];

const sidebarRows = [
  { icon: "🕐", label: "Availability Status", value: "Not Available at\nBhubaneswar Clinic", valueClass: "red" },
  { icon: "👤", label: "Life Stage",           value: "Perimenopause / Menopause", valueClass: "" },
  { icon: "🎯", label: "Primary Concern",      value: "Hot flashes, sleep and mood changes", valueClass: "" },
  { icon: "🛡️", label: "Safety Status",       value: "Routine", valueClass: "green" },
];

const trustItems = [
  { icon: "🛡️", title: "Clinically guided",        desc: "Programs designed by women's health experts with evidence." },
  { icon: "🔒", title: "Private & secure",          desc: "Your data is protected with industry-leading security." },
  { icon: "👥", title: "Women's health specialists", desc: "Access to experienced gynecologists and care coordinators." },
  { icon: "💜", title: "Personalized pathways",     desc: "Care plans tailored to your needs and life stage." },
];

// ── Component ──
export default function ProgramNotAvailable({ onBack, onViewOtherPrograms, onBookConsultation }) {
  const [requested, setRequested] = useState(false);

  return (
    <div className={styles.root}>
      <div className={styles.container}>

        {/* Back link */}
        <button className={styles.backLink} onClick={onBack || (() => {})}>
          ← Back to Program Listing
        </button>

        <div className={styles.mainGrid}>
          {/* ── Left column ── */}
          <div>

            {/* Hero alert */}
            <div className={styles.hero}>
              <div className={styles.heroIcon}>⚠️</div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className={styles.heroTitle}>Program Not Available</div>
                <div className={styles.heroSub}>
                  This program is currently not available at your selected clinic,
                  but we can still help you with the right next step.
                </div>
              </div>
              <div className={styles.unavailableBadge}>
                <span style={{ fontSize: 13 }}>ℹ️</span> Currently Unavailable
              </div>
            </div>

            {/* Program banner */}
            <div className={styles.progBanner}>
              <div className={styles.progEmoji}>{program.emoji}</div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "var(--ava-color-heading)", marginBottom: 4 }}>
                  {program.name}
                </div>
                <div style={{ fontSize: 12, color: "var(--ava-color-caption)", lineHeight: 1.5 }}>
                  {program.short}
                </div>
              </div>
              <div className={styles.clinicNotice}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ava-color-warning-500)", marginBottom: 2 }}>
                    This program is not available<br />at {program.clinic} right now.
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.4 }}>
                    We're here to help you find the right alternative.
                  </div>
                </div>
              </div>
            </div>

            {/* Why this may happen */}
            <div className={styles.card} style={{ marginBottom: 16 }}>
              <div className={styles.sectionTitle}>Why this may happen</div>
              <div className={styles.reasonsGrid}>
                {reasons.map((r, i) => (
                  <div key={i} className={styles.reasonCard}>
                    <div className={styles.reasonIcon}>{r.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ava-color-heading)", marginBottom: 4 }}>
                      {r.title}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.4 }}>
                      {r.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommended alternatives */}
            <div className={styles.card} style={{ marginBottom: 16 }}>
              <div className={styles.sectionTitle}>Recommended alternatives</div>
              <div className={styles.altGrid}>
                {alternatives.map((a, i) => (
                  <div key={i} className={styles.altCard}>
                    <div className={styles.altIcon} style={{ background: a.bg }}>{a.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ava-color-heading)", lineHeight: 1.3 }}>
                      {a.title}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.5, flex: 1 }}>
                      {a.desc}
                    </div>
                    <button
                      className={`${styles.btn} ${styles[`btn${a.ctaVariant}`]} ${styles.btnFull}`}
                      style={{ marginBottom: 0, marginTop: 4 }}
                      onClick={i === 0 ? onBookConsultation : i === 2 ? onViewOtherPrograms : undefined}
                    >
                      {a.cta === "Book Consultation" ? "📅 " : a.cta === "Request Callback" ? "📞 " : "🔍 "}
                      {a.cta}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Similar programs */}
            <div className={styles.card} style={{ marginBottom: 0 }}>
              <div className={styles.sectionTitle}>Similar programs you can explore</div>
              <div className={styles.simGrid}>
                {similarPrograms.map((p, i) => (
                  <div key={i} className={styles.simCard}>
                    <div className={styles.simEmoji}>{p.emoji}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ava-color-heading)", marginBottom: 6, lineHeight: 1.3 }}>
                      {p.name}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.4, marginBottom: 10 }}>
                      {p.desc}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                      {p.tags.map(t => (
                        <span key={t} className={`${styles.badge} ${styles.badgePurple}`}>{t}</span>
                      ))}
                    </div>
                    <button className={`${styles.btn} ${styles.btnGhost} ${styles.btnFull}`} style={{ marginBottom: 0 }}>
                      Learn More →
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust bar */}
            <div className={styles.trustBar}>
              {trustItems.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>{t.icon}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>{t.title}</div>
                    <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.4 }}>{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right sidebar ── */}
          <div>
            {/* Program summary card */}
            <div className={styles.sidebarSummary}>
              <div className={styles.sidebarHeader}>Selected Program Summary</div>
              <div className={styles.sidebarProg}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg, var(--ava-color-secondary-50), var(--ava-color-primary-100))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                  {program.emoji}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "var(--ava-color-heading)", lineHeight: 1.3 }}>
                  {program.name}
                </div>
              </div>
              {sidebarRows.map((row, i) => (
                <div key={i} className={styles.sidebarRow}>
                  <div className={styles.sidebarLabel}>
                    <span style={{ fontSize: 14 }}>{row.icon}</span>
                    <span>{row.label}</span>
                  </div>
                  <div
                    className={styles.sidebarVal}
                    style={{
                      color: row.valueClass === "red"   ? "var(--ava-color-error-500)"   :
                             row.valueClass === "green" ? "var(--ava-color-success-500)" :
                             "var(--ava-color-heading)",
                      whiteSpace: "pre-line",
                      textAlign: "right",
                    }}
                  >
                    {row.value}
                  </div>
                </div>
              ))}

              {/* Recommended next step */}
              <div style={{ padding: "14px 18px", borderTop: "1px solid var(--ava-border-color)", background: "var(--ava-color-primary-bg)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ava-color-heading)", marginBottom: 3 }}>
                      Recommended next step
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.5 }}>
                      Book a gynecology consultation or request a callback for guidance.
                    </div>
                  </div>
                </div>
                <button
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnFull}`}
                  onClick={() => setRequested(r => !r)}
                >
                  {requested ? "✅ Callback Requested!" : "📞 Request Callback"}
                </button>
                <button
                  className={`${styles.btn} ${styles.btnSecondary} ${styles.btnFull}`}
                  onClick={onBookConsultation}
                >
                  📅 Book Consultation
                </button>
                <button
                  className={`${styles.btn} ${styles.btnGhost} ${styles.btnFull}`}
                  style={{ marginBottom: 0 }}
                  onClick={onViewOtherPrograms}
                >
                  🔍 View Other Programs
                </button>
              </div>
            </div>

            {/* Care navigator card */}
            <div className={styles.navigatorCard}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Need help choosing?</div>
                  <div style={{ fontSize: 11, color: "var(--ava-color-caption)", lineHeight: 1.5, marginBottom: 14 }}>
                    Our care navigator can help you understand your options and find the right next step.
                  </div>
                  <button className={`${styles.btn} ${styles.btnGhost} ${styles.btnFull}`} style={{ marginBottom: 0 }}>
                    💬 Chat with Care Navigator
                  </button>
                </div>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: "linear-gradient(135deg, var(--ava-color-secondary-50), var(--ava-color-primary-100))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>
                  👩‍💼
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}